<script setup lang="ts">
import { trackEvent } from '@afinius/utilities/analytics';
import { classNames } from '@afinius/utilities/class-names';
import { isString, isUndefined } from 'lodash';
import { computed, ref } from 'vue';
import { RouteLocation, RouteLocationRaw, useRoute, useRouter } from 'vue-router';

interface Props {
  href?: string;
  to?: RouteLocationRaw;
  type?: 'submit' | 'button' | 'reset';
  ariaLabel?: string;
  ariaExpanded?: boolean;
  borderRadius?: 'xs' | 'sm' | 'md' | 'lg';
  tabIndex?: number;
  component?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  hasHover?: boolean;
  hasNoOutline?: boolean;
  shouldOpenBlank?: boolean;
  onClick?: (event: Event) => void;
}

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  type: 'button',
  href: undefined,
  ariaLabel: undefined,
  ariaExpanded: undefined,
  component: undefined,
  isActive: undefined,
  onClick: undefined,
  tabIndex: 0,
  borderRadius: undefined,
});

const route = useRoute();
const router = useRouter();

const button = ref<HTMLElement>();

const isActive = computed(() =>
  isUndefined(props.isActive)
    ? props.to && router.resolve(props.to as RouteLocationRaw).name === route.name
    : props.isActive,
);

const handleClick = (event: Event) => {
  if (props.type === 'submit') {
    return;
  }

  event.preventDefault();

  if (props.to) {
    trackEvent({
      category: 'Navigation',
      action: 'click',
      label: isString(props.to)
        ? props.to
        : (((props.to as RouteLocation).name || 'unknown') as string),
    });

    if (props.shouldOpenBlank) {
      const route = router.resolve(props.to as RouteLocationRaw);
      return window.open(route.href, '_blank');
    } else {
      return router.push(props.to);
    }
  }

  if (props.href) {
    if (props.shouldOpenBlank) {
      return window.open(props.href, '_blank');
    } else {
      return window.open(props.href, '_self');
    }
  }

  trackEvent({
    category: 'Navigation',
    action: 'click',
    label: button.value?.textContent || 'unknown',
  });

  return props.onClick?.(event);
};

const component = computed(() => {
  if (props.component) return props.component;
  if (props.href) return 'a';
  return 'button';
});

const ariaExpandedAttribute = computed(() => {
  if (isUndefined(props.ariaExpanded)) return;
  return props.ariaExpanded ? 'true' : 'false';
});
</script>

<template>
  <component
    :is="component"
    ref="button"
    :href="href"
    class="clickable"
    :class="classNames({ borderRadius, isActive, isDisabled, hasHover, hasNoOutline })"
    :type="component === 'button' ? type : undefined"
    :disabled="component === 'button' ? isDisabled : undefined"
    :onClick="handleClick"
    :ariaLabel="ariaLabel"
    :ariaExpanded="ariaExpandedAttribute"
    :tabindex="tabIndex"
  >
    <slot />
  </component>
</template>

<style lang="scss">
@use './Clickable.scss';
</style>
